import { useDashboardUIActions } from "@contexts/dashboardUI";
import { useProjectActions } from "@contexts/projects";
import { checkIfCubBased, checkLabel, formatNumberToCurrency, formatNumberToLocale } from "@utils/formatNumber";
import { itemIndexedBudgetDisplay } from "@utils/indexedBudget";
import { useProjectContext } from "@contexts/projects";
import { isBuildingTrial } from "@utils/checkPermiossion";
import { useAuthContext } from "@contexts/auth";
import {
  AMPLI_SERIES_LABELS,
  filterCashFlowColumns,
  isAmpliCurveVisible,
  physicalFinancialLabel,
} from "../cashFlowPlanning";
import { metricPresentation, metricReasonStyle } from "@utils/metricPresentation";

const { useState, useCallback, useEffect, Fragment } = require("react")

const valuePresentation = (value, indexedToToday, conversion = {}) => {
  const available = typeof value === "number" && Number.isFinite(value);
  const indexerAvailable = !conversion.cubBased || (
    typeof conversion.indexerValue === "number"
    && Number.isFinite(conversion.indexerValue)
    && conversion.indexerValue !== 0
  );
  const status = !available
    ? "source_unavailable"
    : !indexerAvailable ? "no_basis" : "ok";
  return metricPresentation({
    id: "projection_value",
    value: status === "ok" ? checkIfCubBased(value, indexedToToday) : null,
    status,
  }, {
    formatter: formatNumberToLocale,
    reasonId: conversion.reasonId,
  });
};

const defaultRow = (label, value, compare, indexedToToday = false, conversion = {}) => {
  const available = typeof value === "number" && Number.isFinite(value);
  const presented = valuePresentation(value, indexedToToday, conversion);
  return <div className="data">
    <span className="label">{label}</span>
    <span className={`value ${(available && compare && compare < value) ? 'color-red' : ''}`}>
      <span aria-describedby={presented.describedBy} data-status={presented.status}>{presented.text}</span>
      {presented.reason && (
        <span id={presented.describedBy} style={metricReasonStyle}>{presented.reason}</span>
      )}
    </span>
  </div>
}

const scheduleRow = (label, value, conversion = {}) => {
  const presented = valuePresentation(value, false, conversion);
  return <div className="data" aria-label={`${label} ${presented.available ? "disponível" : "indisponível"}`}>
    <span className="label">{label}</span>
    <span className="value" aria-describedby={presented.describedBy} data-status={presented.status}>{presented.text}</span>
    {presented.reason && (
      <span id={presented.describedBy} style={metricReasonStyle}>{presented.reason}</span>
    )}
  </div>
}

const unavailableExtraValuesRow = () => (
  <div className="data" aria-label="Estouros / Economias indisponíveis">
    <span className="label">Estouros / Economias</span>
    <span className="value">Indisponível</span>
  </div>
)

const TableItem = ({ item, type, typeTitle, ChildAccordionComponent }) => {
  const { toggleItemDetailOffcanvas } = useDashboardUIActions();
  const { user: { configurations: { projection: { columns: configuredColumns } } } } = useAuthContext();
  const { selectDetail, updateProjectionTableDate } = useProjectActions();
  const { activeProject, filterDocs, selectedGraph, advancedItems, filterDates, cubBased } = useProjectContext();
  const columns = filterCashFlowColumns(configuredColumns, activeProject.cashFlowPlanning);
  const ampliCurveVisible = isAmpliCurveVisible(activeProject);
  const reasonKey = String(item?.wbs_code_id ?? item?.building_unit_id ?? "item")
    .replace(/[^a-zA-Z0-9_-]/g, "-");
  const conversionFor = (slot, indexedToToday = false) => ({
    cubBased,
    indexerValue: indexedToToday
      ? activeProject?.indexer?.value
      : activeProject?.originalIndexer?.value,
    reasonId: `metric-financial-projection-${reasonKey}-${slot}-reason`,
  });
  const [toggle, setToggle] = useState(false)
  const onShowDetail = useCallback((e) => {
    updateProjectionTableDate(e)
    selectDetail(item, type)
    toggleItemDetailOffcanvas()
  }, [item, type])

  const getLevel = () => {
    const shadesOfGray = ['#f8f8f8', '#f0f0f0', '#e8e8e8', '#e0e0e0', '#d8d8d8'];
    let res;
    switch (type) {
      case 'unit':
        res = shadesOfGray[0]
        break;

      case 'cell':
        res = shadesOfGray[1]
        break;

      case 'step':
        res = shadesOfGray[2]
        break;

      case 'substep':
        res = shadesOfGray[3]
        break;

      case 'service':
        res = shadesOfGray[4]
        break;

      default:
        break;
    }
    return { backgroundColor: res }
  }

  const wbsFlat = advancedItems?.map(i => i.wbs_code_id)

  const getAdvBorder = () => {
    if (selectedGraph !== 'advancementGraph') return ''
    if (wbsFlat.some(i => i.includes(item.wbs_code_id))) {
      if (wbsFlat.some(i => i === item.wbs_code_id)) {
        return { backgroundColor: '#cce7c9' }
      }
      return { borderColor: '#a1ca9c', zIndex: 1 }
    } else {
      return ''
    }
  }

  const getMargin = (type) => {
    switch (type) {
      case 'unit':
        return { marginLeft: '0px' }
      case 'cell':
        return { marginLeft: '8px' }
      case 'step':
        return { marginLeft: '16px' }
      case 'substep':
        return { marginLeft: '24px' }
      case 'service':
        return { marginLeft: '32px' }
      default:
        return {}
    }
  }

  const getStyles = () => {
    const level = getLevel()
    const adv = getAdvBorder()
    return { ...level, ...adv }
  }

  const getDatesBetween = () => {
    const dates = [];
    const startDate = new Date(filterDates.beginAt);
    const endDate = new Date(filterDates.endAt);

    startDate.setDate(1);
    endDate.setDate(1);
    startDate.setMonth(startDate.getMonth() + 1);
    endDate.setMonth(endDate.getMonth() + 1);

    while (startDate <= endDate) {
      const month = String(startDate.getMonth() + 1).padStart(2, '0');
      const year = startDate.getFullYear();
      dates.push(`${month}/${year}`);
      startDate.setMonth(startDate.getMonth() + 1);
    }

    return dates;
  };

  const allDates = getDatesBetween(filterDates.beginAt, filterDates.endAt);

  const getMonthlyValue = (date, field, missingValue = 0) => {
    if (!Array.isArray(item.monthlyProjection?.[field]) || item.monthlyProjection[field].length === 0) return missingValue
    const foundItem = item.monthlyProjection[field].find(i => `${String(i.month).padStart(2, "0")}/${i.year}` === date);
    return foundItem ? foundItem.value : missingValue;
  }

  const getValueToReduce = (date, arr) => {
    const itens = filterDocs[arr].filter(j => date === j.date)
    if (itens.length === 0) return 0;

    const field = `wbs_code_id_` + type

    let total = 0;
    itens.forEach(i => {
      i.itens.forEach(j => {
        if (j[field] === item.wbs_code_id) {
          total += j.value;
        }
      })
    });

    return total
  }

  const handleToggle = () => {
    setToggle(!toggle)
  }

  const checkCard = (i, j) => {
    const renderDefaultRow = (label, value, compare, indexedToToday = false) => defaultRow(
      label,
      value,
      compare,
      indexedToToday,
      conversionFor(`${i.replace("/", "-")}-${j}`, indexedToToday),
    );
    const renderScheduleRow = (label, value) => scheduleRow(
      label,
      value,
      conversionFor(`${i.replace("/", "-")}-${j}`),
    );
    switch (j) {
      case 'physical':
        return renderScheduleRow(
          checkLabel(['physical_financial', 'combined'].includes(activeProject.cashFlowPlanning?.effectiveSource)
            ? physicalFinancialLabel(activeProject.cashFlowPlanning)
            : 'Físico'),
          getMonthlyValue(i, 'physical', null),
        )
      case 'predicted':
        return renderScheduleRow(checkLabel('Previsto'), getMonthlyValue(i, 'predicted', null))
      case 'baseline':
        return renderScheduleRow(checkLabel('Linha de base'), getMonthlyValue(i, 'baseline', null))
      case 'disbursement':
        return renderDefaultRow(checkLabel('Desembolso'), getMonthlyValue(i, 'disbursement'))
      case 'ampliDisbursement':
        return renderDefaultRow(
          checkLabel(AMPLI_SERIES_LABELS.ampliDisbursement),
          ampliCurveVisible ? getMonthlyValue(i, 'ampliDisbursement', null) : null,
        )
      case 'cashProjection':
        return renderDefaultRow(
          checkLabel(AMPLI_SERIES_LABELS.cashProjection),
          ampliCurveVisible ? getMonthlyValue(i, 'cashProjection', null) : null,
        )
      case 'measured':
        return renderScheduleRow(checkLabel('Medido'), getMonthlyValue(i, 'measured', null))
      case 'paid':
        return renderDefaultRow(checkLabel('Pago'), getMonthlyValue(i, 'paid'))
      case 'toPay':
        return renderDefaultRow(checkLabel('A pagar'), getMonthlyValue(i, 'toPay'))
      case 'paidToPay':
        return renderDefaultRow(checkLabel('Comprometido'), (getMonthlyValue(i, 'toPay') + (getMonthlyValue(i, 'paid'))))
      case 'availableBudget': {
        const planned = getMonthlyValue(i, 'physical', null);
        return planned === null
          ? renderScheduleRow(checkLabel('Verba disponível'), null)
          : renderDefaultRow(checkLabel('Verba disponível'), (planned - (getMonthlyValue(i, 'toPay') + (getMonthlyValue(i, 'paid')))))
      }
      // case 'availablePos':
      //   return defaultRow(checkLabel('Disponível Positivo'), item.available?.positive)
      // case 'availableNeg':
      //   return defaultRow(checkLabel('Disponível Negativo'), item.available?.negative)
      case 'extraValues':
        if (item.monthlyProjection?.errors?.extraValues) return unavailableExtraValuesRow()
        return renderDefaultRow(checkLabel('Estouros / Economias'), getMonthlyValue(i, 'extraValues'))
      // Em CUB ≡ Orçado (÷ base) — coluna visível, nunca divergente (decisão Leo 30/07)
      case 'indexedBudget':
        return renderDefaultRow(checkLabel('Orçamento Indexado'), itemIndexedBudgetDisplay(item, activeProject?.indexedBudgetMethod, cubBased), undefined, !cubBased)

      default:
        break;
    }
  }


  return (

    <div className="item" style={getMargin(type)}>
      <div className="accordion-item-body" style={{ ...getStyles(), display: "block" }}>
        <div className="column-span-2-tablet">
          <div className="flex wrap-wrap wrap-nowrap-tablet gap-16 align-center">
            <div className="flex align-center gap-8 accordion-sticky-header" style={{ ...getLevel() }}>
              {ChildAccordionComponent && (
                <button
                  className={`accordion-toggle ${toggle ? "active" : ""}`}
                  onClick={handleToggle}
                ></button>
              )}
              <div className="grid">
                <span className="accordion-label">EAP #{item.building_unit_id}{item.wbs ? `.${item.wbs}` : ""} / {typeTitle}</span>
                <span className="accordion-title" >{item.description ?? item.building_unit_name}</span>
              </div>
            </div>
            {allDates.map(i => {
              return <div key={i} style={{ width: `${columns.length * 95}px`, position: 'relative' }} onClick={() => onShowDetail(i)}>
                <div className="month-divider" />
                <p className="table-date">{i}</p>
                <div className={`accordion-content columns-${columns.length}-tablet`}>
                  {columns.map(j => {
                    return <Fragment key={j}>{checkCard(i, j)}</Fragment>
                  })}
                </div>
              </div>
            })}
          </div>
        </div>
      </div>
      {toggle && <ChildAccordionComponent parent={item.wbs_code_id} />}
    </div >
  )
}

export default TableItem;
